import { useState, useEffect, useRef } from "react";
import PlacesAutocompleteInput from "../common/PlacesAutocompleteInput";

// ═══════════════════════════════════════════════════════════════════════════
// MAP TOOL RAIL
// ═══════════════════════════════════════════════════════════════════════════
// Vertical rail down the left edge of the map, CoStar-style. Consolidates
// controls that were previously scattered across three separate overlay
// clusters on the right edge (zoom, basemap, parcel/street view) plus the
// boundary and radius sections that lived in the sidebar.
//
// Every button here drives behaviour that exists. Corridor is deliberately
// absent — there is no code behind it, and a rail of dead buttons is worse
// than a short rail.

const RAIL_W = 54;

export default function MapToolRail({
  S,
  zoom,          // { onIn, onOut }
  basemap,       // { value, onChange }
  parcel,        // { active, onToggle }
  streetView,    // { active, onToggle }
  boundary,      // see BoundaryPanel below
  radius,        // see RadiusPanel below
  locationPanel, // rendered inside the Location popover
  locationCount, // number of location fields currently filtering
  travel,        // see TravelPanel below
}) {
  const [open, setOpen] = useState(null);   // "location" | "polygon" | "radius" | "travel" | "map" | null
  const wrapRef = useRef(null);

  // Close the open popover on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(null);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(null); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const boundaryActive = !!boundary.ring || boundary.drawMode;
  const radiusActive = !!radius.center || radius.drawMode;
  const travelActive = !!travel?.ring || !!travel?.drawMode;

  function railBtn({ key, icon, label, active, onClick, onClear }) {
    return (
      <div key={key} style={{ position: "relative" }}>
        <button
          onClick={onClick}
          title={label}
          style={{
            width: RAIL_W - 8, minHeight: 46,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
            background: active ? S.red : S.chipBg,
            backdropFilter: "blur(4px)",
            border: `1px solid ${active ? S.red : S.border}`,
            borderRadius: 7,
            color: active ? "#ffffff" : S.text,
            cursor: "pointer", padding: "5px 2px",
            fontFamily: S.font, transition: "background 0.15s, border-color 0.15s",
          }}>
          <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 0.2, lineHeight: 1 }}>{label}</span>
        </button>
        {onClear && active && (
          <button
            onClick={e => { e.stopPropagation(); onClear(); }}
            title={`Clear ${label.toLowerCase()}`}
            style={{
              position: "absolute", top: -6, right: -6, width: 17, height: 17,
              borderRadius: "50%", background: S.sidebar, border: `1px solid ${S.border}`,
              color: S.text, fontSize: 10, fontWeight: 800, lineHeight: 1,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
            }}>✕</button>
        )}
      </div>
    );
  }

  const popover = (children, width = 300) => (
    <div style={{
      position: "absolute", left: RAIL_W + 4, top: 0, width,
      background: S.sidebar, border: `1px solid ${S.border}`, borderRadius: 10,
      boxShadow: "0 10px 32px rgba(0,0,0,0.45)", padding: 13, zIndex: 30,
      fontFamily: S.font, color: S.text,
    }}>{children}</div>
  );

  return (
    <div ref={wrapRef} style={{ position: "absolute", top: 10, left: 10, zIndex: 550, display: "flex", flexDirection: "column", gap: 5, width: RAIL_W - 8 }}>

      {/* Zoom */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {[["+", zoom.onIn], ["−", zoom.onOut]].map(([label, fn], i) => (
          <button key={label} onClick={fn} title={label === "+" ? "Zoom in" : "Zoom out"}
            style={{
              width: RAIL_W - 8, height: 30, background: S.chipBg, backdropFilter: "blur(4px)",
              border: `1px solid ${S.border}`, borderTop: i === 1 ? "none" : undefined,
              borderRadius: i === 0 ? "7px 7px 0 0" : "0 0 7px 7px",
              color: S.text, fontSize: 16, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
            }}>{label}</button>
        ))}
      </div>

      {/* Location — market / submarket / city */}
      <div style={{ position: "relative" }}>
        {railBtn({
          key: "location", icon: "▦", label: "Location", active: locationCount > 0 || open === "location",
          onClick: () => setOpen(o => (o === "location" ? null : "location")),
        })}
        {locationCount > 0 && (
          <span style={{ position: "absolute", top: -5, left: -5, minWidth: 15, height: 15, borderRadius: 8, background: S.red, color: "#ffffff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px", boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>
            {locationCount}
          </span>
        )}
        {open === "location" && popover(locationPanel, 330)}
      </div>

      {/* Polygon */}
      <div style={{ position: "relative" }}>
        {railBtn({
          key: "polygon", icon: "✏️", label: "Polygon", active: boundaryActive,
          onClick: () => setOpen(o => (o === "polygon" ? null : "polygon")),
          onClear: boundary.ring ? boundary.onClear : (boundary.drawMode ? boundary.onCancelDraw : null),
        })}
        {open === "polygon" && popover(<BoundaryPanel S={S} boundary={boundary} />, 320)}
      </div>

      {/* Radius */}
      <div style={{ position: "relative" }}>
        {railBtn({
          key: "radius", icon: "◎", label: "Radius", active: radiusActive,
          onClick: () => setOpen(o => (o === "radius" ? null : "radius")),
          onClear: radius.center ? radius.onClear : null,
        })}
        {open === "radius" && popover(<RadiusPanel S={S} radius={radius} />, 320)}
      </div>

      {/* Travel — approximate drive-time ring */}
      <div style={{ position: "relative" }}>
        {railBtn({
          key: "travel", icon: "🚗", label: "Travel", active: travelActive,
          onClick: () => setOpen(o => (o === "travel" ? null : "travel")),
          onClear: travel?.ring ? travel.onClear : null,
        })}
        {open === "travel" && popover(<TravelPanel S={S} travel={travel} />, 320)}
      </div>

      {/* Parcel */}
      {railBtn({
        key: "parcel", icon: "⊕", label: "Parcel", active: parcel.active, onClick: parcel.onToggle,
      })}

      {/* Street View */}
      {railBtn({
        key: "sv", icon: "🚶", label: "Street", active: streetView.active, onClick: streetView.onToggle,
      })}

      {/* Map type */}
      <div style={{ position: "relative" }}>
        {railBtn({
          key: "map", icon: "🗺", label: "Map", active: open === "map",
          onClick: () => setOpen(o => (o === "map" ? null : "map")),
        })}
        {open === "map" && popover(
          <>
            <PanelTitle S={S}>Base map</PanelTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {[
                { key: "satellite", icon: "🛰", label: "Satellite" },
                { key: "street", icon: "🗺", label: "Street" },
                { key: "hybrid", icon: "🌐", label: "Hybrid" },
              ].map(b => {
                const on = basemap.value === b.key;
                return (
                  <button key={b.key} onClick={() => { basemap.onChange(b.key); setOpen(null); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                      background: on ? `${S.red}1f` : "transparent",
                      border: `1px solid ${on ? S.red : S.border}`, borderRadius: 7,
                      color: S.text, padding: "7px 10px", fontSize: 11, fontWeight: 700,
                      fontFamily: S.font, cursor: "pointer",
                    }}>
                    <span>{b.icon}</span>{b.label}
                  </button>
                );
              })}
            </div>
          </>, 190)}
      </div>
    </div>
  );
}

function PanelTitle({ S, children }) {
  return (
    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.7, textTransform: "uppercase", color: S.muted, marginBottom: 8 }}>
      {children}
    </div>
  );
}

// ── Polygon popover ────────────────────────────────────────────────────────
function BoundaryPanel({ S, boundary }) {
  const btn = (active, extra = {}) => ({
    background: active ? S.red : "transparent",
    border: `1px solid ${active ? S.red : S.border}`,
    borderRadius: 7, color: active ? "#ffffff" : S.text,
    padding: "7px 9px", fontSize: 10.5, fontWeight: 800, fontFamily: S.font,
    cursor: "pointer", ...extra,
  });

  return (
    <>
      <PanelTitle S={S}>Boundary</PanelTitle>

      <button onClick={boundary.onToggleDraw}
        style={{ ...btn(boundary.drawMode), width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 6 }}>
        <span>✏️</span>
        <span>{boundary.drawMode
          ? `Drawing… ${boundary.draftCount} pt${boundary.draftCount === 1 ? "" : "s"}`
          : boundary.ring ? "Redraw boundary" : "Draw boundary"}</span>
      </button>

      {boundary.drawMode && (
        <>
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            <button onClick={boundary.onCommit} disabled={boundary.draftCount < 3}
              style={{ ...btn(boundary.draftCount >= 3), flex: 1, opacity: boundary.draftCount < 3 ? 0.5 : 1, cursor: boundary.draftCount < 3 ? "not-allowed" : "pointer" }}>
              Finish
            </button>
            <button onClick={boundary.onUndo} disabled={boundary.draftCount === 0}
              style={{ ...btn(false), flex: 1, opacity: boundary.draftCount === 0 ? 0.5 : 1 }}>Undo</button>
            <button onClick={boundary.onCancelDraw} style={{ ...btn(false), flex: 1, color: S.muted }}>Cancel</button>
          </div>
          <div style={{ fontSize: 9.5, color: S.muted, marginBottom: 8, lineHeight: 1.5 }}>
            Click corners on the map. Click the first point, double-click, or press Enter to close. Backspace undoes, Esc cancels.
          </div>
        </>
      )}

      {boundary.ring && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 7, padding: "6px 8px", background: `${S.red}14`, border: `1px solid ${S.red}33`, borderRadius: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {boundary.label || "Custom boundary"} · {boundary.areaSqMi} sq mi · {boundary.pinCount} pin{boundary.pinCount === 1 ? "" : "s"}
            </span>
            <button onClick={boundary.onClear} title="Clear boundary"
              style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 12, padding: "0 2px", lineHeight: 1, flexShrink: 0 }}>✕</button>
          </div>

          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            <input
              value={boundary.nameInput}
              onChange={e => boundary.onNameInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") boundary.onSave(); }}
              placeholder="Name this submarket…"
              disabled={!boundary.storeReady}
              style={{ flex: 1, background: S.card, border: `1px solid ${S.border}`, borderRadius: 7, padding: "7px 9px", color: S.text, fontSize: 11, fontFamily: S.font, outline: "none", boxSizing: "border-box", opacity: boundary.storeReady ? 1 : 0.5 }} />
            <button onClick={boundary.onSave}
              disabled={!boundary.storeReady || boundary.saving || !boundary.nameInput.trim()}
              style={{ ...btn(true), flexShrink: 0, opacity: (!boundary.storeReady || boundary.saving || !boundary.nameInput.trim()) ? 0.5 : 1 }}>
              {boundary.saving ? "…" : "Save"}
            </button>
          </div>

          {!boundary.storeReady && (
            <div style={{ fontSize: 9, color: S.muted, marginBottom: 8, lineHeight: 1.4 }}>
              Saving needs <code>migrations/018_map_boundaries.sql</code> run in Supabase.
            </div>
          )}
        </>
      )}

      {boundary.saved.length > 0 && (
        <>
          <PanelTitle S={S}>Saved submarkets</PanelTitle>
          <div style={{ maxHeight: 190, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
            {boundary.saved.map(b => {
              const active = boundary.label === b.name;
              return (
                <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <button onClick={() => boundary.onApplySaved(b)}
                    title={b.createdBy ? `Saved by ${b.createdBy}` : b.name}
                    style={{ ...btn(false), flex: 1, textAlign: "left", background: active ? `${S.red}1f` : "transparent", borderColor: active ? S.red : S.border, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {b.name}
                  </button>
                  <button onClick={() => boundary.onDeleteSaved(b)} title={`Delete "${b.name}"`}
                    style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 12, padding: "0 3px", lineHeight: 1, flexShrink: 0 }}>🗑</button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

// ── Radius popover ─────────────────────────────────────────────────────────
function RadiusPanel({ S, radius }) {
  const btn = (active) => ({
    background: active ? S.red : "transparent",
    border: `1px solid ${active ? S.red : S.border}`,
    borderRadius: 7, color: active ? "#ffffff" : S.text,
    padding: "7px 9px", fontSize: 10.5, fontWeight: 800, fontFamily: S.font, cursor: "pointer",
  });

  return (
    <>
      <PanelTitle S={S}>Enter location and radius</PanelTitle>

      <div style={{ display: "flex", gap: 4, marginBottom: 7 }}>
        <PlacesAutocompleteInput
          value={radius.input}
          onChange={radius.onInput}
          onSelect={({ address }) => { radius.onInput(address); setTimeout(() => radius.onApply(), 0); }}
          types={["geocode"]}
          onKeyDown={e => { if (e.key === "Enter") radius.onApply(); }}
          placeholder="Address, place, or lat/lng…"
          style={{ flex: 1, background: S.card, border: `1px solid ${S.border}`, borderRadius: 7, padding: "7px 9px", color: S.text, fontSize: 11, fontFamily: S.font, outline: "none", boxSizing: "border-box" }}
        />
        <button onClick={radius.onApply} disabled={radius.geocoding || !radius.input.trim()}
          style={{ ...btn(true), flexShrink: 0, opacity: (radius.geocoding || !radius.input.trim()) ? 0.5 : 1 }}>
          {radius.geocoding ? "…" : "Apply"}
        </button>
      </div>

      <button onClick={radius.onToggleDraw}
        style={{ ...btn(radius.drawMode), width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 9 }}>
        <span>📍</span>
        <span>{radius.drawMode ? "Click on the map…" : "Set centre on map"}</span>
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, color: S.muted, fontWeight: 600, marginBottom: 4 }}>
        <span>Radius</span>
        <span style={{ color: radius.center ? S.red : S.muted, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
          {radius.miles < 1 ? `${(radius.miles * 5280).toFixed(0)} ft` : `${radius.miles.toFixed(radius.miles < 10 ? 1 : 0)} mi`}
        </span>
      </div>
      <input type="range" min="0.25" max="25" step="0.25" value={radius.miles}
        onChange={e => radius.onMiles(Number(e.target.value))}
        style={{ width: "100%", accentColor: "#e93529", marginBottom: 8 }} />

      {radius.center && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", background: `${S.red}14`, border: `1px solid ${S.red}33`, borderRadius: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={radius.center.label}>
            📍 {radius.center.label}
          </span>
          <button onClick={radius.onClear} style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 12, padding: "0 2px", lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>
      )}
    </>
  );
}

// ── Travel popover ─────────────────────────────────────────────────────────
function TravelPanel({ S, travel }) {
  const btn = (active) => ({
    background: active ? S.red : "transparent",
    border: `1px solid ${active ? S.red : S.border}`,
    borderRadius: 7, color: active ? "#ffffff" : S.text,
    padding: "7px 9px", fontSize: 10.5, fontWeight: 800, fontFamily: S.font, cursor: "pointer",
  });

  return (
    <>
      <PanelTitle S={S}>Drive time</PanelTitle>

      <button onClick={travel.onToggleDraw}
        style={{ ...btn(travel.drawMode), width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 8 }}>
        <span>📍</span>
        <span>{travel.drawMode ? "Click the map…" : travel.origin ? "Move origin" : "Set origin on map"}</span>
      </button>

      {travel.origin && (
        <div style={{ fontSize: 10, color: S.muted, marginBottom: 8 }}>
          Origin {travel.origin.lat.toFixed(4)}, {travel.origin.lng.toFixed(4)}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, color: S.muted, fontWeight: 600, marginBottom: 4 }}>
        <span>Minutes</span>
        <span style={{ color: S.red, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{travel.minutes} min</span>
      </div>
      <input type="range" min="5" max="60" step="5" value={travel.minutes}
        onChange={e => travel.onMinutes(Number(e.target.value))}
        style={{ width: "100%", accentColor: "#e93529", marginBottom: 9 }} />

      <button onClick={travel.onApply}
        disabled={!travel.origin || travel.busy}
        style={{ ...btn(true), width: "100%", marginBottom: 8, opacity: (!travel.origin || travel.busy) ? 0.5 : 1, cursor: (!travel.origin || travel.busy) ? "not-allowed" : "pointer" }}>
        {travel.busy ? "Measuring…" : `Apply — about ${travel.estimate} billed lookups`}
      </button>

      {travel.ring && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 8, padding: "6px 8px", background: "rgba(138,138,138,0.10)", border: "1px solid rgba(138,138,138,0.35)", borderRadius: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a" }}>
            {travel.meta?.minutes ?? travel.minutes} min · approximate
          </span>
          <button onClick={travel.onClear}
            style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 12, padding: "0 2px", lineHeight: 1 }}>✕</button>
        </div>
      )}

      {travel.meta?.unreachableRays > 0 && (
        <div style={{ fontSize: 9.5, color: S.muted, lineHeight: 1.45, marginBottom: 6 }}>
          {travel.meta.unreachableRays} of 16 directions had no drivable route — usually water.
          Those sides of the shape are estimated from the rest.
        </div>
      )}

      <div style={{ fontSize: 9.5, color: S.muted, lineHeight: 1.45 }}>
        Approximate: the reachable area is sampled along 16 directions, not
        traced road by road. Treat the edge as indicative, not exact.
      </div>
    </>
  );
}
